import type cytoscape from 'cytoscape'

import { LAYOUT_OPTIONS } from '../../layout'

import {
  computeDepths,
  getChildren,
  isVisible,
  refreshExpandCollapseState,
  setCollectionVisible,
  setVisible,
} from './visibility'

// Badge dimensions (must match styles.ts)
const BADGE_WIDTH = 36
const BADGE_HEIGHT = 18
const BADGE_OFFSET_Y = 6
const TOGGLE_ANIMATION_DURATION = 400

/**
 * Runs layout on visible elements and recenters the viewport to the visible
 * nodes after the layout completes without changing the current zoom level.
 */
export function runLayoutAndRecenter(
  cy: cytoscape.Core,
  options?: {
    animate?: boolean
    onLayoutStop?: () => void
  },
): void {
  const visibleElements = cy.elements().filter(el => isVisible(el))
  if (visibleElements.length === 0) {
    options?.onLayoutStop?.()
    return
  }

  const { animate = false, onLayoutStop } = options ?? {}
  const zoom = cy.zoom()
  const layout = visibleElements.layout({
    ...LAYOUT_OPTIONS,
    animate,
    animationDuration: animate ? TOGGLE_ANIMATION_DURATION : 0,
    fit: false,
  } as cytoscape.LayoutOptions)

  layout.on('layoutstop', () => {
    const visibleNodes = cy.nodes().filter(node => isVisible(node))
    if (visibleNodes.length > 0) {
      cy.zoom(zoom)
      if (animate) {
        cy.animate({
          center: { eles: visibleNodes },
          duration: TOGGLE_ANIMATION_DURATION,
        })
      } else {
        cy.center(visibleNodes)
      }
    }
    onLayoutStop?.()
  })
  layout.run()
}

/**
 * Runs layout on visible elements while preserving the current viewport.
 */
export function runLayoutPreservingViewport(
  cy: cytoscape.Core,
  options?: runLayoutPreservingViewport.Options,
): void {
  const visibleElements = cy.elements().filter(el => isVisible(el))
  if (visibleElements.length === 0) {
    options?.onLayoutStop?.()
    return
  }

  const { animate = false, onLayoutStop } = options ?? {}
  const zoom = cy.zoom()
  const pan = cy.pan()
  const layout = visibleElements.layout({
    ...LAYOUT_OPTIONS,
    animate,
    animationDuration: animate ? TOGGLE_ANIMATION_DURATION : 0,
    fit: false,
  } as cytoscape.LayoutOptions)

  layout.on('layoutstop', () => {
    cy.zoom(zoom)
    cy.pan({
      x: pan.x,
      y: pan.y,
    })
    onLayoutStop?.()
  })
  layout.run()
}
export namespace runLayoutPreservingViewport {
  export interface Options {
    animate?: boolean
    onLayoutStop?: () => void
  }
}

/**
 * Expands a node by showing its direct children.
 */
export function expandNode(node: cytoscape.NodeSingular): void {
  const sourcePosition = node.position()

  getChildren(node).forEach(child => {
    const childNode = child as cytoscape.NodeSingular
    childNode.position(sourcePosition)
  })

  setCollectionVisible(node.outgoers(), true)
  refreshExpandCollapseState(node.cy())
  runLayoutPreservingViewport(node.cy(), {
    animate: true,
  })
}

/**
 * Collapses a node by hiding all its descendants.
 */
export function collapseNode(node: cytoscape.NodeSingular): void {
  const cy = node.cy()
  setCollectionVisible(node.successors(), false)
  refreshExpandCollapseState(cy)
  runLayoutAndRecenter(cy, {
    animate: true,
  })
}

/**
 * Expands all nodes in the graph.
 */
export function expandAll(cy: cytoscape.Core): void {
  cy.nodes().forEach(node => {
    setVisible(node, true)
  })
  refreshExpandCollapseState(cy)
  runLayoutPreservingViewport(cy, {
    animate: true,
  })
}

/**
 * Collapses the graph to show only nodes up to the specified depth limit.
 */
export function collapseToDepth(cy: cytoscape.Core, depthLimit: number): void {
  const depths = computeDepths(cy)
  cy.nodes().forEach(node => {
    const depth = depths.get(node.id())
    if (depth === undefined || depth > depthLimit) {
      setVisible(node, false)
    } else {
      setVisible(node, true)
    }
  })
  refreshExpandCollapseState(cy)
  runLayoutAndRecenter(cy, {
    animate: true,
  })
}

/**
 * Finds a node whose expand/collapse badge was clicked at the given position.
 */
export function findNodeWithBadgeAt(
  cy: cytoscape.Core,
  clickPosition: cytoscape.Position,
): cytoscape.NodeSingular | null {
  let foundNode: cytoscape.NodeSingular | null = null

  cy.nodes().forEach(node => {
    if (foundNode) {
      return
    }

    // Skip hidden nodes
    if (!isVisible(node)) {
      return
    }

    // Skip nodes without children
    const children = getChildren(node)
    if (children.length === 0) {
      return
    }

    // Get node's position and calculate badge bounds
    const nodePosition = node.position()
    const nodeHeight = node.height()
    const nodeBottom = nodePosition.y + nodeHeight / 2

    // Badge positioning matches styles.ts:
    // - background-position-y: 100% puts image CENTER at node bottom
    // - background-offset-y: BADGE_OFFSET_Y shifts it down
    // So badge center is at: nodeBottom + BADGE_OFFSET_Y
    // And badge top is at: nodeBottom + BADGE_OFFSET_Y - BADGE_HEIGHT/2
    const badgeCenterX = nodePosition.x
    const badgeCenterY = nodeBottom + BADGE_OFFSET_Y
    const badgeTop = badgeCenterY - BADGE_HEIGHT / 2 - 10 // Extra 10px tolerance at top
    const badgeBottom = badgeCenterY + BADGE_HEIGHT / 2
    const badgeLeft = badgeCenterX - BADGE_WIDTH / 2
    const badgeRight = badgeCenterX + BADGE_WIDTH / 2

    // Check if click is within badge bounds
    if (
      clickPosition.x >= badgeLeft &&
      clickPosition.x <= badgeRight &&
      clickPosition.y >= badgeTop &&
      clickPosition.y <= badgeBottom
    ) {
      foundNode = node
    }
  })

  return foundNode
}

/**
 * Checks if all nodes in the graph are currently expanded (visible).
 */
export function isAllNodesExpanded(cy: cytoscape.Core): boolean {
  let allVisible = true
  cy.nodes().forEach(node => {
    if (!isVisible(node)) {
      allVisible = false
    }
  })
  return allVisible
}
